import { FC } from 'react';
import styled, { css } from 'styled-components';

/* eslint-disable @typescript-eslint/no-unused-vars */
const ComponentWithProps: FC<{ readOnly?: boolean }> = ({
  readOnly,
  ...props
}) => {
  return <label {...props} />;
};

const Container = styled(ComponentWithProps)`
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  margin-right: 0.75rem;

  label {
    margin-left: 0.5rem;
    cursor: pointer;
  }

  ${({ readOnly }) =>
    readOnly
      ? css`
          cursor: default !important;
          & * {
            cursor: default !important;
          }
        `
      : ''}
`;

const Input = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
  &:checked ~ span {
    border: 1px solid var(--primary-600);
    &::after {
      background-color: var(--primary-600);
    }
  }

  &:focus ~ span {
    outline: var(--outline);
  }
`;

const Checkmark = styled.span`
  display: grid;
  place-items: center;
  border: 1px solid var(--gray-300);
  stroke: transparent;
  height: 1rem;
  width: 1rem;
  margin: 0.125rem 0;
  aspect-ratio: 1/1;
  position: relative;
  border-radius: 50%;
  &::after {
    content: '';
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
  }
  outline-width: 1px !important;
`;

export type Props = {
  label?: string | React.ReactElement;
  checked?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  readOnly?: boolean;
  id?: string | number;
  name?: string;
  tabIndex?: number | undefined;
};
/** Given an `id` and a `name`, the value of a radio is `id + '_' + name`.
 * Therefore, to extract the name and use it in a `handleChange` function,
 * the `getRadioValue` function must be used.
 * @example
 * const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
 *  console.log(
 *    getRadioValue(e.target.value) // logs 'blue'
 * )
 * }
 *
 * <Radio name="color" id="blue" onChange={handleChange}/> // the value is `blue_color`
 */
const Radio: FC<Props> = ({
  label,
  checked,
  onChange,
  id,
  name,
  readOnly,
  tabIndex,
}) => {
  return (
    <Container readOnly={readOnly}>
      <Input
        type="radio"
        checked={checked}
        value={id + '_' + name}
        onChange={onChange}
        id={id + '_' + name}
        name={name}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : tabIndex}
      />
      <Checkmark></Checkmark>
      <label htmlFor={id + '_' + name}>{label}</label>
    </Container>
  );
};

export const getRadioValue = (value: string): string => {
  return value.split('_').slice(0, -1).join('_');
};

export default Radio;
